// Stijlprofielen voor de scriptgenerator: per filmtype de richtlijnen, de beats
// en de voorbeelden waaruit de stijl geleerd wordt. Fase 1 van
// docs/scriptgenerator-plan.md — de generatie zelf komt later; hier staat de
// kennis die nu nog hardgecodeerd in de Explainer zit.
//
// Bewust open voor iedereen met een login (zoals de concepttrainer): wie de
// stijl van een explainer kent is de regisseur, niet de beheerder.
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson } from '../../../utils/apiFout';
import { bevestig } from '../../../utils/bevestig';

const SOORTEN = [
  { key: 'eigen_script', label: 'Eigen script' },
  { key: 'film_transcript', label: 'Transcript eigen film' },
  { key: 'extern', label: 'Externe referentie' },
];
const soortLabel = (k) => SOORTEN.find((s) => s.key === k)?.label || k;

export default function Scriptstijlen() {
  const [profielen, setProfielen] = useState([]);
  const [gekozen, setGekozen] = useState(null); // typeKey
  const [detail, setDetail] = useState(null); // { profiel, voorbeelden }
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuw, setNieuw] = useState({ kind: 'eigen_script', titel: '', tekst: '', bron: '', oordeel: 'goed', notities: '' });

  const laadLijst = () => authFetch(`${BACKEND_URL}/Api/scriptstijlen`)
    .then((r) => leesJson(r, 'Stijlprofielen laden'))
    .then((d) => {
      setProfielen(d.profielen || []);
      setGekozen((huidig) => huidig || d.profielen?.[0]?.typeKey || null);
    })
    .catch((e) => setFout(e.message))
    .finally(() => setLaden(false));

  useEffect(() => { laadLijst(); }, []);

  useEffect(() => {
    if (!gekozen) return;
    setDetail(null);
    authFetch(`${BACKEND_URL}/Api/scriptstijlen/${gekozen}`)
      .then((r) => leesJson(r, 'Stijlprofiel laden'))
      .then(setDetail)
      .catch((e) => setFout(e.message));
  }, [gekozen]);

  const bewaarProfiel = async (velden) => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/scriptstijlen/${gekozen}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
      });
      await leesJson(r, 'Opslaan');
      toast.success('Stijlprofiel opgeslagen');
      laadLijst();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const voegVoorbeeldToe = async () => {
    if (!nieuw.titel.trim() || !nieuw.tekst.trim()) { toast.error('Titel en tekst zijn verplicht'); return; }
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/scriptstijlen/${gekozen}/voorbeelden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nieuw),
      });
      const d = await leesJson(r, 'Voorbeeld toevoegen');
      setDetail((v) => ({ ...v, voorbeelden: [d.voorbeeld, ...(v?.voorbeelden || [])] }));
      setNieuwOpen(false);
      setNieuw({ kind: 'eigen_script', titel: '', tekst: '', bron: '', oordeel: 'goed', notities: '' });
      toast.success('Voorbeeld toegevoegd');
      laadLijst();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const wisselOordeel = async (v) => {
    const nieuwOordeel = v.oordeel === 'goed' ? 'matig' : 'goed';
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/scriptstijlen/voorbeelden/${v.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ oordeel: nieuwOordeel }),
      });
      const d = await leesJson(r, 'Oordeel bijwerken');
      setDetail((s) => ({ ...s, voorbeelden: s.voorbeelden.map((x) => (x.id === v.id ? d.voorbeeld : x)) }));
      laadLijst();
    } catch (e) { toast.error(e.message); }
  };

  const verwijder = async (v) => {
    if (!(await bevestig({
      titel: 'Voorbeeld verwijderen?',
      tekst: `"${v.titel}" telt daarna niet meer mee voor de stijl. De tekst blijft bewaard.`,
      knop: 'Verwijderen',
    }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/scriptstijlen/voorbeelden/${v.id}`, { method: 'DELETE' });
      await leesJson(r, 'Verwijderen');
      setDetail((s) => ({ ...s, voorbeelden: s.voorbeelden.filter((x) => x.id !== v.id) }));
      laadLijst();
    } catch (e) { toast.error(e.message); }
  };

  if (laden) return <CircularProgress size={22} />;

  const p = detail?.profiel;
  const voorbeelden = detail?.voorbeelden || [];
  const goede = voorbeelden.filter((v) => v.oordeel === 'goed').length;

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per filmtype de schrijfstijl: de richtlijnen die de generator meekrijgt, de opbouw in beats,
        en de voorbeelden waaruit die richtlijnen worden afgeleid. Alleen voorbeelden die je als
        <b> goed</b> aanmerkt tellen mee — trainen op alles levert gemiddeld werk op.
      </Typography>
      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {profielen.map((x) => (
          <Chip key={x.typeKey} label={`${x.naam} · ${x.aantalGoed}`} onClick={() => setGekozen(x.typeKey)}
            color={x.typeKey === gekozen ? 'primary' : 'default'}
            variant={x.typeKey === gekozen ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
        ))}
      </Stack>

      {!p ? <CircularProgress size={20} /> : (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{p.naam}</Typography>
            <Typography variant="body2" color="text.secondary">{p.omschrijving}</Typography>
            <Typography variant="caption" color="text.secondary">
              Gripp-projecttypes: {(p.projecttypes || []).join(', ') || '—'} · profielversie {p.versie}
              {p.afgeleidOp ? ` · afgeleid op ${p.afgeleidOp.slice(0, 10)}` : ' · nog niet afgeleid uit voorbeelden'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Opbouw</Typography>
            <Stack spacing={0.5}>
              {(p.structuur || []).map((b, i) => (
                <Typography key={`${b.beat}-${i}`} variant="body2">
                  <b>{b.beat}</b> — {b.doel}
                </Typography>
              ))}
            </Stack>
          </Box>

          <RichtlijnVeld waarde={p.richtlijnen || ''} bezig={bezig}
            onBewaar={(tekst) => bewaarProfiel({ richtlijnen: tekst })} />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Voorbeelden ({goede} van de {voorbeelden.length} tellen mee)
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setNieuwOpen(true)} sx={{ textTransform: 'none' }}>
                + Voorbeeld toevoegen
              </Button>
            </Stack>
            {voorbeelden.length === 0 ? (
              <Alert severity="info">
                Nog geen voorbeelden. Plak een script dat je goed vindt, of het transcript van een film
                die we hebben opgeleverd — daar wordt de stijl straks uit afgeleid.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {voorbeelden.map((v) => (
                  <Box key={v.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5,
                    opacity: v.oordeel === 'goed' ? 1 : 0.65 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{v.titel}</Typography>
                      <Chip size="small" label={soortLabel(v.kind)} sx={{ height: 20, fontSize: 11 }} />
                      <Typography variant="caption" color="text.secondary">{v.woorden} woorden</Typography>
                      {v.bron && <Typography variant="caption" color="text.secondary">· {v.bron}</Typography>}
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title={v.oordeel === 'goed' ? 'Telt mee — klik om uit te zetten' : 'Telt niet mee — klik om aan te zetten'}>
                        <Chip size="small" label={v.oordeel === 'goed' ? 'Goed' : 'Matig'} onClick={() => wisselOordeel(v)}
                          color={v.oordeel === 'goed' ? 'success' : 'default'} sx={{ height: 22, fontWeight: 700 }} />
                      </Tooltip>
                      <Button size="small" color="error" onClick={() => verwijder(v)} sx={{ textTransform: 'none' }}>
                        Verwijderen
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5,
                      whiteSpace: 'pre-wrap', maxHeight: 60, overflow: 'hidden' }}>
                      {v.tekst.slice(0, 320)}{v.tekst.length > 320 ? '…' : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      <Dialog open={nieuwOpen} onClose={() => setNieuwOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Voorbeeld toevoegen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select size="small" label="Soort" value={nieuw.kind}
              onChange={(e) => setNieuw({ ...nieuw, kind: e.target.value })}>
              {SOORTEN.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Titel" value={nieuw.titel}
              onChange={(e) => setNieuw({ ...nieuw, titel: e.target.value })} />
            <TextField size="small" label="Bron (projectnummer, link of bestandsnaam)" value={nieuw.bron}
              onChange={(e) => setNieuw({ ...nieuw, bron: e.target.value })} />
            <TextField multiline minRows={10} label="Tekst" value={nieuw.tekst}
              onChange={(e) => setNieuw({ ...nieuw, tekst: e.target.value })}
              placeholder="Plak hier het script of het transcript." />
            <TextField size="small" multiline minRows={2} label="Waarom is dit goed (of juist niet)?"
              value={nieuw.notities} onChange={(e) => setNieuw({ ...nieuw, notities: e.target.value })}
              helperText="Deze notitie gaat mee in de analyse — hier leert de generator het meeste van." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNieuwOpen(false)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={voegVoorbeeldToe} disabled={bezig} sx={{ textTransform: 'none' }}>
            Toevoegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Los component: een textarea binnen de ouder zou bij elke toetsaanslag remounten. */
function RichtlijnVeld({ waarde, bezig, onBewaar }) {
  const [tekst, setTekst] = useState(waarde);
  useEffect(() => { setTekst(waarde); }, [waarde]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Richtlijnen</Typography>
      <TextField fullWidth multiline minRows={8} value={tekst} onChange={(e) => setTekst(e.target.value)}
        sx={{ '& textarea': { fontSize: 13.5, lineHeight: 1.6 } }} />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" variant="contained" disabled={bezig || tekst === waarde}
          onClick={() => onBewaar(tekst)} sx={{ textTransform: 'none' }}>
          Richtlijnen opslaan
        </Button>
        {tekst !== waarde && <Button size="small" onClick={() => setTekst(waarde)} sx={{ textTransform: 'none' }}>Herstellen</Button>}
      </Stack>
    </Box>
  );
}
